// ═══════════════════════════════════════════════════════════════════════════════════════════════
// Document folders & file types — THE one source (doc_folders / doc_types, migration 2026-10-07_doc_folders_types).
// Replaces the vocabulary copies in code (see docs/folders-and-file-types-report.md). Loaded once per session and
// cached; the admin screen refreshes it after an edit.
//
// A folder/type has a stable KEY (stored on documents as folder_key / type_key), an editable display NAME, and a
// legacy_name — the exact string historically stored in folder_name / file_type, which never changes, so old
// writers and old rows still resolve. Group and compare by KEY; show NAME.
// ═══════════════════════════════════════════════════════════════════════════════════════════════
import { useEffect, useState } from 'react';
import { supabase } from '../supabase';

const bySort = (a, b) => (a.sort - b.sort) || a.name.localeCompare(b.name);

// The three filing levels, in display order. A document is filed against a property (or one of its parcels), a
// client, or a consultant firm (file_exchange.consultant_id — migration 2026-10-08_consultant_doc_level).
export const DOC_LEVELS = ['property', 'client', 'consultant'];

// Pure: rows → the lookup object every screen uses. Unit-tested (src/__tests__/doc-vocab.test.js).
export function indexVocab(folderRows, typeRows) {
  const folders = [...(folderRows || [])].sort(bySort);
  const types = [...(typeRows || [])].sort(bySort);
  const folderByKey = new Map(folders.map(f => [f.key, f]));
  const typeByKey = new Map(types.map(t => [t.key, t]));
  const lc = (s) => String(s ?? '').trim().toLowerCase();
  // Display names first, stored (legacy) names last, so a stored name always wins. Folder display names are unique only
  // WITHIN a level (the consultant "Contracts" is stored as "Consultant Contracts"; the client one as "Contracts"), so
  // "Contracts" must resolve to the folder whose stored name it is, not whichever level sorts last.
  const typeByAnyName = new Map();
  for (const t of types) typeByAnyName.set(lc(t.name), t);
  for (const t of types) typeByAnyName.set(lc(t.legacy_name), t);
  const folderByAnyName = new Map();
  for (const f of folders) folderByAnyName.set(lc(f.name), f);
  for (const f of folders) folderByAnyName.set(lc(f.legacy_name), f);
  return {
    folders, types,
    folder: (key) => folderByKey.get(key) || null,
    type: (key) => typeByKey.get(key) || null,
    // A type given as a key, the stored (legacy) name or the current display name → the type row, or null.
    resolveType: (v) => (v == null || v === '') ? null : (typeByKey.get(v) || typeByAnyName.get(lc(v)) || null),
    resolveFolder: (v) => (v == null || v === '') ? null : (folderByKey.get(v) || folderByAnyName.get(lc(v)) || null),
    // Real folders only (never the Intake pile states), in display order.
    foldersByLevel: (level, { includeRetired = false } = {}) =>
      folders.filter(f => !f.system && f.level === level && (includeRetired || f.active)),
    typesIn: (folderKey, { includeRetired = false } = {}) =>
      types.filter(t => t.folder_key === folderKey && (includeRetired || t.active)),
    // What the Year means in a folder (doc_folders.year_label), e.g. "Effective year"; plain "Year" when unset.
    yearLabel: (folderKey) => folderByKey.get(folderKey)?.year_label || 'Year',
  };
}

// The keys the CODE writes or tests directly. Every one is LOCKED in the table (migration 2026-10-07_doc_folders_types):
// staff can rename it, but never retire or move it, so these literals stay valid. Writers set keys only — the
// database trigger (doc_keys_sync) fills folder_name / file_type from them.
export const DOC_KEYS = {
  notices: 'notices', taxBills: 'tax_bills', appealResults: 'appeal_results',
  clientUploads: 'client_uploads', needToReview: 'need_to_review', other: 'other', taskAttachments: 'task_attachments',
  assessmentNotice: 'assessment_notice', taxBill: 'tax_bill', taxBillTransmittal: 'tax_bill_transmittal',
};
// The Intake pile: no folder yet, Client Uploads or Other (the Need-to-Review pile is need_review, read separately).
// A PostgREST .or() filter, so the pile is narrowed IN THE QUERY: filtering in the browser after the read let the
// 1,000-row response cap hide a pile's newest files once its uploader had dropped 1,000 files in all (2026-11-03).
export const INTAKE_PILE_OR = `folder_key.is.null,folder_key.eq.${DOC_KEYS.clientUploads},folder_key.eq.${DOC_KEYS.other}`;

let cache = null;
// Resolves to the index. ⛔ AN EMPTY RESULT IS AN ERROR, not "no folders": both tables are RLS-protected, and a read
// the policy blocks returns 0 rows with no error — the documented silent-success class. Never cache a failure.
export function loadDocVocab({ fresh = false } = {}) {
  if (!cache || fresh) {
    cache = (async () => {
      const [{ data: f, error: fe }, { data: t, error: te }] = await Promise.all([
        supabase.from('doc_folders').select('key,name,legacy_name,level,has_years,year_label,sort,active,system,locked'),
        supabase.from('doc_types').select('key,folder_key,name,legacy_name,sort,active,scan_as,bill_type,locked'),
      ]);
      if (fe || te) throw new Error(`Document folders/types could not be loaded: ${(fe || te).message}`);
      if (!f?.length || !t?.length) throw new Error('Document folders/types came back empty (0 rows) — a permissions problem, not an empty list.');
      return indexVocab(f, t);
    })();
    cache.catch(() => { cache = null; });
  }
  return cache;
}

// React: { vocab, error } — vocab is null until loaded.
export function useDocVocab() {
  const [state, setState] = useState({ vocab: null, error: null });
  useEffect(() => {
    let alive = true;
    loadDocVocab().then(v => alive && setState({ vocab: v, error: null }), e => alive && setState({ vocab: null, error: e }));
    return () => { alive = false; };
  }, []);
  return state;
}
